import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AutocompleteOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface AutocompleteProps {
  id?: string;
  label?: string;
  error?: string;
  helperText?: string;
  helperInfo?: string; // shown only when clicking the (i) icon
  options: AutocompleteOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  allowCustom?: boolean;
  clearOnSelect?: boolean; // when true, clears input after selecting an option (for multi-add UIs)
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  id,
  label,
  error,
  helperText,
  helperInfo,
  options,
  value,
  onChange,
  placeholder = 'Selecione uma opção',
  required = false,
  className,
  disabled = false,
  allowCustom = true,
  clearOnSelect = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<AutocompleteOption[]>(options);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showHelperInfo, setShowHelperInfo] = useState(false);
  
  const autocompleteId = id || `autocomplete-${Math.random().toString(36).substr(2, 9)}`;

  // Determine if current input is a custom (typed) value (not present in options)
  const isCustomInput = useMemo(() => {
    const trimmedValue = inputValue?.toString().trim() || '';
    if (!trimmedValue) return false;
    
    // Check if the input matches any option (case insensitive and ignoring accents)
    const normalizedInput = trimmedValue.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    return !options.some(opt => {
      const normalizedLabel = opt.label?.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
      const normalizedValue = opt.value?.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
      return normalizedLabel === normalizedInput || normalizedValue === normalizedInput;
    });
  }, [inputValue, options]);

  // Normalize external value to a string to avoid rendering [object Object]
  const normalizeValue = (v: unknown): string => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number') return String(v);
    if (typeof v === 'object') {
      // Try common shapes: { value, label }
      const anyv = v as any;
      if (typeof anyv.value === 'string' || typeof anyv.value === 'number') {
        return String(anyv.value);
      }
      return '';
    }
    return '';
  };

  // Set initial display value based on selected value or raw value when custom
  useEffect(() => {
    const vStr = normalizeValue(value);
    if (vStr) {
      const selectedOption = options.find(option => option.value === vStr);
      setInputValue(selectedOption?.label || vStr);
    } else {
      setInputValue('');
    }
  }, [value, options]);

  // Filter options based on input (coerce to string to avoid runtime errors)
  useEffect(() => {
    const iv = (inputValue ?? '').toString();
    if (iv === '' && value === '') {
      setFilteredOptions(options);
    } else {
      const needle = iv.toLowerCase();
      const filtered = options.filter(option => {
        const lbl = (option.label ?? '').toString().toLowerCase();
        return lbl.includes(needle);
      });
      setFilteredOptions(filtered);
    }
  }, [inputValue, options, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current && 
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        
        // Reset input to selected value if user clicks away without selecting
        if (value) {
          const selectedOption = options.find(option => option.value === value);
          setInputValue(selectedOption?.label || '');
        } else {
          setInputValue('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [options, value]);

  // Helper to commit a custom value (when not in options)
  const commitCustomValue = () => {
    if (!allowCustom || disabled) return;
    const v = (inputValue ?? '').toString().trim();
    if (v !== '' && !options.some(o => o.label === v || o.value === v)) {
      onChange(v);
      setIsOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  };

  const handleOptionClick = (option: AutocompleteOption) => {
    onChange(option.value);
    setInputValue(clearOnSelect ? '' : option.label);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setInputValue('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="w-full">
      {label && (
        <div className="flex items-center justify-between">
          <label htmlFor={autocompleteId} className="form-label">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {helperInfo && (
            <button
              type="button"
              aria-label="Informação"
              className="text-gray-600 hover:text-gray-800 text-xs font-semibold px-1 py-0.5 border border-gray-300 rounded"
              onClick={() => setShowHelperInfo((s) => !s)}
            >
              (i)
            </button>
          )}
        </div>
      )}

      {showHelperInfo && helperInfo && (
        <div className="mb-1 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1">
          {helperInfo}
        </div>
      )}
      
      <div className="relative">
        <div className="relative flex items-center">
          <input
            id={autocompleteId}
            ref={inputRef}
            type="text"
            className={cn(
              'form-input pr-10 w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 placeholder-gray-400 dark:placeholder-gray-500',
              isCustomInput && 'border-amber-400 focus:ring-amber-500 text-amber-800 placeholder-amber-500',
              error && 'form-input-error',
              className
            )}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (filteredOptions.length > 0) {
                  handleOptionClick(filteredOptions[0]);
                } else if (allowCustom) {
                  commitCustomValue();
                }
              }
            }}
            onBlur={() => {
              // Allow committing custom value when leaving the field
              if (isCustomInput && allowCustom) {
                commitCustomValue();
              }
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
            {inputValue && (
              <button
                type="button"
                onClick={handleClear}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                tabIndex={-1}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              tabIndex={-1}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        {isOpen && (
          <div 
            ref={dropdownRef}
            className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={cn(
                    "cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100",
                    option.disabled && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={() => !option.disabled && handleOptionClick(option)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="text-gray-500 dark:text-gray-400 py-2 px-3">Nenhuma opção encontrada</div>
            )}
          </div>
        )}
      </div>
      
      {error && (
        <p className="form-error">
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p className="form-help">
          {helperText}
        </p>
      )}

      {!error && isCustomInput && inputValue.trim() !== '' && (
        <button
          type="button"
          className="mt-1 text-sm text-amber-700 hover:text-amber-800 underline"
          onClick={() => {
            commitCustomValue();
            // optional: keep focus consistent
            inputRef.current?.blur();
          }}
          title="Clique para confirmar este novo valor"
        >
          Novo valor: "{inputValue}"
        </button>
      )}
    </div>
  );
};

export default Autocomplete;
